import React, { useEffect, useState, useRef, useCallback } from "react"; //eslint-disable-line
import he from "he";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { v4 as uuidv4 } from "uuid";
import classNames from "classnames";
import { Remarkable } from "remarkable";

/* Styles */
import "../../../components/chat/chat.css";

/* Services */
import { fetchAssistantChatGPT } from "../../../services/chat.service";
import {
  getIteractionById,
  getSessionsByUseCase,
} from "../../../services/teacherSession.service";

/* Zustand stores */

import useGlobalStore from "../../../store/useGlobalStore";
import useSessionStore from "../../../store/useSessionStore";

/* Components */
import Button from "../../../components/button/button";
import Bubble from "../../../components/Bubble/Bubble";
import UseCaseHistory from "../../../components/UseCaseHistory/UseCaseHistory";
import WritingContent from "../../../components/globals/WritingContent/WritingContent";
import BottomMenuMobile from "../../../components/globals/BottomMenuMobile/BottomMenuMobile";
import InputChatContainer from "../../../components/globals/InputChatContainer/InputChatContainer";
import ExtensiveWritingContent from "../../../components/globals/ExtensiveWritingContent/ExtensiveWritingContent";

/* Utils */
import {
  exportToDocx,
  htmlToPdf,
  removeHtmlElements,
} from "../../../utils/Utils";

/* Assets */
import ArrowBack from "../../../assets/icons/arrow_back.svg";
import HistoryEmpty from "../../../assets/icons/history_toggle_off.svg";

/* Hooks */
import useTimer from "../../../hooks/useTimer";

const ChatViewTemplate = () => {
  /* States */
  const [showExtendedLoadingMessage, setShowExtendedLoadingMessage] =
    useState(false);
  const [history, setHistory] = useState([]);
  const [isView, setIsView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isActive, setIsActive] = useState(false);
  /* eslint-disable */
  /* Para efectos del template de codigo lo dejamos por default en 5, puede cambiarse el numero sin problema */
  const [statusIteraction, setStatusIteraction] = useState(5);
  /* eslint-enable */
  const [messagesStep1, setMessagesStep1] = useState([]);
  const [messagesStep2, setMessagesStep2] = useState([]);
  const [messagesStep3, setMessagesStep3] = useState([]);
  const [messages, setMessages] = useState([]);
  const [resMessages, setResMessages] = useState([]);
  const [isChatActive, setIsChatActive] = useState(false);
  const [isEditorOn] = useState(false);

  /* Hooks */

  const { gptSuccess, setCreditAnimateClear } = useGlobalStore();
  const navigate = useNavigate();
  const temporalLoadingRef = useRef(false);
  const parentSessionIdRef = useRef(null);
  const sessionIdRef = useRef(null);
  /* eslint-disable */
  /* Para efectos del template de codigo se apaga */
  const stepPrompt = useRef(1);
  /* eslint-enable */
  const chatContainerRef = useRef(null);
  const stepsRef = useRef(1);
  const isMobile = useMediaQuery({ maxWidth: 564 }); //eslint-disable-line
  const { t, i18n } = useTranslation(); //eslint-disable-line
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  /* eslint-disable */
  /* Para efectos del template de codigo se apaga */
  const sessionId = queryParams.get("sessionId");
  /* eslint-enable */
  const { user } = useSessionStore();
  const { count, startTimer, resetTimer } = useTimer();

  /* Effects */

  // Observa el contador de tiempo y muestra un mensaje extendido si supera los 30 segundos.
  useEffect(() => {
    if (count > 30) {
      scrollToBottom();
      setShowExtendedLoadingMessage(true);
    }
  }, [count]);

  // Por cada mensaje obtenido de la IA se hace scroll hacia abajo.
  useEffect(() => {
    scrollToBottom();
  }, [messages, statusIteraction]);

  // Cuando la respuesta de fetchAssistantChatGPT finaliza, se estucha el guardado en el state resMessages
  useEffect(() => {
    if (resMessages.length) {
      setMessagesByStep(resMessages);
      setNewMessage("");
      if (temporalLoadingRef.current) temporalLoadingRef.current = false;
      const messageAssistands = resMessages.filter(
        (e) => e?.role === "assistant" || e?.type === "ai" || e?.role === "ai"
      );
      if (messageAssistands.length) {
        const lastAssistand = messageAssistands[messageAssistands.length - 1];
        setMessages([...messages, { ...lastAssistand }]);
      }
      if (sessionIdRef.current) {
        sessionByIdMutation.mutate(sessionIdRef.current);
      }
      setLoading(false);
    }
  }, [resMessages]); //eslint-disable-line

  /*
  Historial de super poder, siempre se busca por el ultimo paso ejemmplo ultimo paso "enad_class3"
  Para efectos del template de codigo no se pasa un super poder real
  */
  useEffect(() => {
    teacherSessionMutation.mutate("none");
    // eslint-disable-next-line
  }, []);

  const trackBackEvent = () => {
    if (isMobile) {
      navigate("/");
    }
    setCreditAnimateClear();
  };

  /* Funtions */

  const teacherSessionMutation = useMutation(getSessionsByUseCase, {
    onSuccess: (res) => {
      setHistory(res);
    },
    onError: () => {},
  });

  const showChat = () => {
    setIsChatActive(!isChatActive);
  };

  const hiddenChat = () => {
    setIsChatActive(!isChatActive);
  };

  const scrollToBottom = () => {
    /* Hacemos foco al final de la burbuja cuando hay nuevos mensajes */
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight + 300;
    }
  };

  const conditions = [
    "<stage_1>",
    "<stage_2>",
    "<stage_3>",
    "contextoUsuario",
    "ContextoClase",
    "Contexto del grupo",
    "Eres una asistente para docentes",
  ];

  const getMessagesByStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        return messagesStep1;
      case 2:
        return messagesStep2;
      case 3:
        return messagesStep3;
      default:
        return 1;
    }
  };

  const onExportToWord = async (currentContent) => {
    const md = new Remarkable({
      html: true,
      breaks: true,
      typographer: true,
    });
    let html = md.render(currentContent);
    html = he.decode(html);
    await exportToDocx(html, "Explicar actividad ENAd");
  };

  const onDownloadPDF = async (currentContent) => {
    const md = new Remarkable({
      html: true,
      breaks: true,
      typographer: true,
    });

    let html = md.render(removeHtmlElements(currentContent));
    html = he.decode(html);

    await htmlToPdf(
      "Planear una sesión ENAd",
      `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
      "Explicar actividad ENAd",
      html,
      true
    );
  };

  const toggleClass = () => {
    setIsActive(!isActive);
  };

  // Activar cuando se use el api del chat GPT
  /* eslint-disable */
  const getCurrentStepByUseCase = (slug) => {
    switch (slug) {
      case "enad_class1":
        return 1;
      case "enad_class2":
        return 2;
      case "enad_class3":
        return 3;
      default:
        return 1;
    }
  };
  /* eslint-enable */

  const sessionByIdMutation = useMutation(getIteractionById, {
    onSuccess: (data) => {
      if (data?.data) {
        if (!resMessages.length) {
          /*** 
          Ejemplo usando super poderes con pasos como enad_class1, enad_class2, enad_class3.
          Si es uno solo paso el step siempre es solo 1 segun getCurrentStepByUseCase
          ***/
          /* Ejemplo usando super poderes con pasos como enad_class1, enad_class2, enad_class3 */
          // const currentStepMessages = [];
          // const currentStep = getCurrentStepByUseCase(
          //   data.data.superPower.slug
          // );
          // stepPrompt.current = currentStep;
          // const historyStep1Messages = data?.data?.messages;
          // currentStepMessages.push({
          //   step: currentStep,
          //   messages: historyStep1Messages,
          // });
          // parentSessionIdRef.current = data?.data?.parent_session_id || null;
          // const satinizedMessages = [];
          // currentStepMessages
          //   .sort((a, b) => a.step - b.step)
          //   .forEach((e) => {
          //     satinizedMessages.push(...e.messages);
          //     setMessagesByInitialStep(e.step, e.messages);
          //   });
          // setMessages(satinizedMessages);
          // El estado pasa a mostrar las burbujas de la iteraccion con la IA
          // setStatusIteraction(5);
          /* Para efectos de pruebas con el template default siempre se muestran las burbujas sin conexion con el api */
        } else {
          parentSessionIdRef.current = data?.data?.parent_session_id || null;
        }
      }
    },
    onError: (error) => {},
  });

  /* eslint-disable */
  const mutation = useMutation(fetchAssistantChatGPT, {
    onSuccess: (res) => {
      resetTimer(); // Se reinicia el contador y oculta burbuja de mensaje extendido
      setShowExtendedLoadingMessage(false); // Se oculta burbuja de mensaje extendido
      setLoading(false); // Se oculta burbuja de mensaje "escribiendo ... "
      setResMessages(res?.messages); // Se registra messages del response del api
      scrollToBottom();
    },
    onError: () => {
      resetTimer();
      setShowExtendedLoadingMessage(false);
      setLoading(false);
    },
  });
  /* eslint-enable */

  const setMessagesByStep = (currentMessages) => {
    switch (stepsRef.current) {
      case 1:
        setMessagesStep1(currentMessages);
        break;
      case 2:
        setMessagesStep2(currentMessages);
        break;
      case 3:
        setMessagesStep3(currentMessages);
        break;
      default:
        return 1;
    }
  };
  /* eslint-disable */
  const setMessagesByInitialStep = (initialStep, currentMessages) => {
    switch (initialStep) {
      case 1:
        setMessagesStep1(currentMessages);
        break;
      case 2:
        setMessagesStep2(currentMessages);
        break;
      case 3:
        setMessagesStep3(currentMessages);
        break;
      default:
        return 1;
    }
  };
  /* eslint-enable */

  const send = (message, newSession = false) => {
    startTimer(); // Se calcula el tiempo para validar si se debe de mostrar burbuja con mensaje extendido

    if (message && message.trim() !== "") {
      // Siempre enviamos un solo objeto de message en el arreglo de messages en el payload
      const payload = {
        role: "user",
        content: message === "N/A" ? "" : message,
      };
      // Persistir el paso actual
      getMessagesByStep(stepsRef.current);

      const currrentMessagesList = [...messages, { ...payload }];

      // Lista de mensajes en el chat acumulativo por cada vez que el usuario escribe
      setMessages(currrentMessagesList);
      setNewMessage("");

      /* 
      Booleano que encience la burbuja que dice Escribiendo ....
      Para efectos del template de codigo solo va a mostrar la burbuja de escribiendo y el mensaje extendido, simulando que el api tarda en responder,
      se apaga cuando el api responden con los mensajes
      */
      setLoading(true);

      // Siempre que tenemos iteraccion por primera vez con un paso o un super poder simple, siempre generamos nueva sesion modo automatico
      /* Para efectos del template de codigo queda apagado la session */
      // if (!sessionId) {
      //   sessionIdRef.current = uuidv4();
      //   navigate(`/design-enad-class?sessionId=${sessionIdRef.current}`);
      // }

      // Podemos tambien generar sesiones manualmente pasandole a la funcion send el parametro newSession
      /* Para efectos del template de codigo queda apagado la session */
      // if (sessionId && newSession) {
      //   parentSessionIdRef.current = sessionId;
      //   sessionIdRef.current = uuidv4();
      //   navigate(`/design-enad-class?sessionId=${sessionIdRef.current}`);
      // }

      /* Para efectos del template de codigo queda apagado el api */

      // temporalLoadingRef.current = true;
      // mutation.mutate({
      //   messages: [payload], //eslint-disable-line
      //   type: `enad_class${stepPrompt.current}`,
      //   session_id: (!newSession && sessionId) || sessionIdRef.current,
      //   // Siempre que tenemos pasos en un super poder, paso 2 tiene el parent session de la anterior
      //   parent_session_id: parentSessionIdRef.current || null,
      //   use_case_id: null,
      //   lang: i18n.language,
      //   // Si el super poder selecciona un perfil de curso, el id debe estar en class_profile_id
      //   class_profile_id: null,
      //   asset_ids: [],
      //   response_type: null,
      //   response_schema: null,
      // });
    }
  };

  const sendMessage = useCallback(() => {
    if (newMessage && newMessage.trim() !== "") {
      send(newMessage);
    }
    // eslint-disable-next-line
  }, [newMessage]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter") {
      if (e.shiftKey || e.ctrlKey) {
        // Permite el salto de línea con Shift+Enter o Ctrl+Enter
        return;
      } else {
        // Evita el comportamiento predeterminado de "Enter" en un textarea (añadir una nueva línea)
        e.preventDefault();
        sendMessage();
      }
    }
    // eslint-disable-next-line
  }, []);

  const toggleView = (buttonName, data) => {
    navigate(`/design-enad-class?sessionId=${data.session_id}`);
    setIsView(buttonName);
    if (isActive) {
      toggleClass();
    }
  };

  const filteredMessages = messages
    ?.filter(
      (e) =>
        !conditions.some(
          (stage) =>
            e?.content?.includes(stage) || e?.data?.content.includes(stage)
        )
    )
    .filter((e) => e?.content || e?.data?.content)
    .map((e) => ({ ...e, id: uuidv4() }));

  return (
    <>
      {!isMobile && (
        <div className="back-button">
          <Link to={"/"} className="back-button_arrow" onClick={trackBackEvent}>
            <img src={ArrowBack} className="" alt="" />
            {t("inicio")}
          </Link>
          <span className="back_button_root">{t("classText1")} /</span>
          <span className="back_button_page">{t("enad_clase_titulo")}</span>
        </div>
      )}
      <div className="wrapper editor">
        {/* Historial */}
        <div
          className={`sidebar_history !h-auto max-h-desktopWrapper xl:max-w-96 xl:min-w-80 ${
            isActive ? "active" : ""
          }`}
        >
          <h6 className="blue-text sidebar-title">{t("historialTitulo")}</h6>
          {history.length ? (
            <UseCaseHistory
              history={history}
              sessionId={null}
              isView={isView}
              toggleView={toggleView} //eslint-disable-line
            />
          ) : (
            <>
              <div className="history_empty">
                <img src={HistoryEmpty} alt="empty" />
                <p className="blue-text">{t("historialTituloVacio")}</p>
              </div>
            </>
          )}
          <Button
            label="Cerrar"
            variant="primary-outline"
            onClick={toggleClass}
          />
        </div>

        {/* Chat */}
        <div
          className={classNames(
            "h-full w-full sm-max:max-h-90/100 flex flex-col justify-end md:gap-4 chat-sticky",
            {
              active: isChatActive,
              "xl:max-w-100": isEditorOn,
            }
          )}
        >
          <div
            ref={chatContainerRef}
            className={`flex flex-col flex-grow justify-end h-0 overflow-y-auto relative sm-max:px-0 sm-max:pt-0 sm-max:pb-4 sm-max:bg-transparent ${
              isEditorOn && "px-1 pt-1 pb-3 "
            }`}
          >
            <div className="chat_wrapper-scroll pr-2">
              {/* Burbujas principales de iteraccion */}
              {statusIteraction >= 5 && (
                <>
                  {filteredMessages?.map((message, i) => {
                    return (
                      <Bubble
                        message={message}
                        isEditorOn={isEditorOn}
                        onDownloadPDF={onDownloadPDF}
                        onExportToWord={onExportToWord}
                      />
                    );
                  })}
                  {(loading || temporalLoadingRef.current) && (
                    <Bubble message={{ type: "ai" }}>
                      <WritingContent
                        showExtendedLoadingMessage={showExtendedLoadingMessage}
                      />
                    </Bubble>
                  )}
                  {(loading || temporalLoadingRef.current) &&
                    showExtendedLoadingMessage && (
                      <Bubble message={{ type: "ai" }}>
                        <ExtensiveWritingContent />
                      </Bubble>
                    )}
                </>
              )}
            </div>
          </div>

          <InputChatContainer
            newMessage={newMessage}
            sendMessage={sendMessage}
            loading={loading}
            setNewMessage={setNewMessage}
            handleKeyPress={handleKeyPress}
            temporalLoadingRef={temporalLoadingRef}
          />
          <div className="md:hidden mt-6 w-full flex items-center justify-center">
            <Button
              label="Cerrar"
              variant="primary-outline"
              onClick={hiddenChat}
            />
          </div>
        </div>
      </div>
      {/* Mobile Bottom navbar */}
      {isMobile && (
        <BottomMenuMobile
          showChat={showChat}
          trackBackEvent={trackBackEvent}
          gptSuccess={gptSuccess}
          toggleClass={toggleClass}
        />
      )}
    </>
  );
};

export default ChatViewTemplate;
